/**
 * Companies House Session Management Service
 * Manages authorization status and transaction ID for Companies House filing
 */

const STORAGE_KEY_AUTHORIZED = 'companiesHouseAuthorized'
const STORAGE_KEY_TRANSACTION_ID = 'companiesHouseTransactionId'
const STORAGE_KEY_COMPANY_NUMBER = 'companiesHouseCompanyNumber'
const STORAGE_KEY_EXPIRES_AT = 'companiesHouseExpiresAt'

export interface CompaniesHouseSession {
  isAuthorized: boolean
  transactionId: string | null
  companyNumber: string | null
  expiresAt: string | null
}

class CompaniesHouseSessionService {
  /**
   * Mark user as authorized for Companies House
   * @param companyNumber - The company number that was authorized
   * @param expiresAt - Optional expiration timestamp
   */
  setAuthorized(companyNumber: string, expiresAt?: string): void {
    localStorage.setItem(STORAGE_KEY_AUTHORIZED, 'true')
    localStorage.setItem(STORAGE_KEY_COMPANY_NUMBER, companyNumber)
    if (expiresAt) {
      localStorage.setItem(STORAGE_KEY_EXPIRES_AT, expiresAt)
    }
  }

  /**
   * Check if user is authorized for Companies House
   * Also checks if the session has expired
   */
  isAuthorized(): boolean {
    const authorized = localStorage.getItem(STORAGE_KEY_AUTHORIZED) === 'true'
    
    if (!authorized) {
      return false
    }

    // Check expiration if set
    const expiresAt = localStorage.getItem(STORAGE_KEY_EXPIRES_AT)
    if (expiresAt) {
      const expirationDate = new Date(expiresAt)
      const now = new Date()
      if (now > expirationDate) {
        // Session expired, clear it
        this.clearSession()
        return false
      }
    }

    return true
  }

  /**
   * Store the transaction ID for reuse
   * @param transactionId - The transaction ID from createTransaction
   */
  setTransactionId(transactionId: string): void {
    localStorage.setItem(STORAGE_KEY_TRANSACTION_ID, transactionId)
  }

  /**
   * Get the stored transaction ID
   */
  getTransactionId(): string | null {
    return localStorage.getItem(STORAGE_KEY_TRANSACTION_ID)
  }

  /**
   * Get the company number from session
   */
  getCompanyNumber(): string | null {
    return localStorage.getItem(STORAGE_KEY_COMPANY_NUMBER)
  }

  /**
   * Get the full session data
   */
  getSession(): CompaniesHouseSession {
    return {
      isAuthorized: this.isAuthorized(),
      transactionId: this.getTransactionId(),
      companyNumber: this.getCompanyNumber(),
      expiresAt: localStorage.getItem(STORAGE_KEY_EXPIRES_AT),
    }
  }

  /**
   * Clear the Companies House session
   * Called on logout or when session expires
   */
  clearSession(): void {
    localStorage.removeItem(STORAGE_KEY_AUTHORIZED)
    localStorage.removeItem(STORAGE_KEY_TRANSACTION_ID)
    localStorage.removeItem(STORAGE_KEY_COMPANY_NUMBER)
    localStorage.removeItem(STORAGE_KEY_EXPIRES_AT)
    
    // Also clear sessionStorage items
    sessionStorage.removeItem('companiesHouseAuthRedirect')
    sessionStorage.removeItem('companiesHouseCompanyNumber')
    
    // Clear callback tracking keys
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('ch_callback_')) {
        sessionStorage.removeItem(key)
      }
    })
  }

  /**
   * Update expiration time
   * @param expiresAt - New expiration timestamp
   */
  setExpiresAt(expiresAt: string): void {
    localStorage.setItem(STORAGE_KEY_EXPIRES_AT, expiresAt)
  }
}

export const companiesHouseSessionService = new CompaniesHouseSessionService()

